class VehicleManager {
  constructor() {
    this.MAX_VEHICLES = window.MyGarageSettings?.maxVehicles || 4;
    this.storageKeys = {
      cart: 'my_garage_vehicles',
      local: 'my_garage_vehicles',
      metafield: {
        namespace: 'my_garage',
        key: 'vehicles'
      }
    };
  }

  validateVehicle(vehicle) {
    const requiredFields = ['year', 'make', 'model'];
    const errors = [];

    requiredFields.forEach(field => {
      if (!vehicle[field]) {
        errors.push(`${field} is required`);
      }
    });

    if (!vehicle.id) {
      vehicle.id = this.generateUUID();
    }

    return {
      isValid: errors.length === 0,
      errors,
      vehicle
    };
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  async getVehicles() {
    try {
      // Try getting from customer metafields first
      if (window.customerLoggedIn) {
        const metafieldVehicles = await this.getFromMetafields();
        if (metafieldVehicles) {
          return metafieldVehicles;
        }
      }

      // Try cart attributes next
      const cartVehicles = await this.getFromCart();
      if (cartVehicles) {
        return cartVehicles;
      }

      // Finally, try localStorage
      return this.getFromLocalStorage() || [];
    } catch (error) {
      console.error('Error getting vehicles:', error);
      return [];
    }
  }

  async saveVehicles(vehicles) {
    try {
      // Validate vehicles array
      if (!Array.isArray(vehicles)) {
        throw new Error('Vehicles must be an array');
      }

      // Validate each vehicle
      const validationResults = vehicles.map(v => this.validateVehicle(v));
      const invalidVehicles = validationResults.filter(r => !r.isValid);
      
      if (invalidVehicles.length > 0) {
        throw new Error('Invalid vehicles found: ' + 
          invalidVehicles.map(v => v.errors.join(', ')).join('; '));
      }

      // Save to all storage mechanisms
      const results = await Promise.all([
        this.saveToMetafields(vehicles),
        this.saveToCart(vehicles),
        this.saveToLocalStorage(vehicles)
      ]);

      return results.some(result => result === true);
    } catch (error) {
      console.error('Error saving vehicles:', error);
      return false;
    }
  }

  // Storage-specific methods
  async getFromMetafields() {
    if (!window.customerLoggedIn) return null;

    try {
      const query = `
        query getCustomerVehicles {
          customer {
            metafield(
              namespace: "${this.storageKeys.metafield.namespace}"
              key: "${this.storageKeys.metafield.key}"
            ) {
              value
            }
          }
        }
      `;

      const data = await window.StorefrontAPI.query(query);
      if (data?.customer?.metafield?.value) {
        return JSON.parse(data.customer.metafield.value);
      }
      return null;
    } catch (error) {
      console.error('Error getting from metafields:', error);
      return null;
    }
  }

  async saveToMetafields(vehicles) {
    if (!window.customerLoggedIn) return false;

    try {
      const mutation = `
        mutation customerUpdateMetafield($metafield: CustomerMetafieldsSetInput!) {
          customerMetafieldsSet(metafields: [$metafield]) {
            userErrors {
              field
              message
            }
          }
        }
      `;

      const result = await window.StorefrontAPI.query(mutation, {
        metafield: {
          namespace: this.storageKeys.metafield.namespace,
          key: this.storageKeys.metafield.key,
          value: JSON.stringify(vehicles),
          type: "json"
        }
      });

      return !result.customerMetafieldsSet.userErrors.length;
    } catch (error) {
      console.error('Error saving to metafields:', error);
      return false;
    }
  }

  async getFromCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart.attributes && cart.attributes[this.storageKeys.cart]) {
        return JSON.parse(cart.attributes[this.storageKeys.cart]);
      }
      return null;
    } catch (error) {
      console.error('Error getting from cart:', error);
      return null;
    }
  }

  async saveToCart(vehicles) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [this.storageKeys.cart]: JSON.stringify(vehicles)
          }
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error saving to cart:', error);
      return false;
    }
  }

  getFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.storageKeys.local);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error getting from localStorage:', error);
      return null;
    }
  }

  saveToLocalStorage(vehicles) {
    try {
      localStorage.setItem(this.storageKeys.local, JSON.stringify(vehicles));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  async handleError(error) {
    if (error.message.includes('quota')) {
      // Storage quota exceeded
      this.handleStorageQuota();
    } else if (error.message.includes('offline')) {
      // Network error
      this.queueOperation(error.operation, error.data);
    } else {
      throw error;
    }
  }

  handleStorageQuota() {
    // Try to free up space by removing old data
    try {
      const oldKeys = ['old_vehicles', 'temp_vehicles'];
      oldKeys.forEach(key => localStorage.removeItem(key));
      
      // Compress existing data
      const vehicles = this.getFromLocalStorage();
      if (vehicles) {
        const compressed = this.compressVehicleData(vehicles);
        localStorage.setItem(this.storageKeys.local, compressed);
      }
    } catch (e) {
      console.error('Storage cleanup failed:', e);
      window.MyGarage.showNotification(
        'Storage full - Please delete some vehicles',
        'error'
      );
    }
  }

  compressVehicleData(vehicles) {
    // Remove unnecessary fields and shorten keys
    return vehicles.map(v => ({
      i: v.id,
      y: v.year,
      m: v.make,
      o: v.model,
      d: v.is_default || false
    }));
  }

  decompressVehicleData(compressed) {
    return compressed.map(v => ({
      id: v.i,
      year: v.y,
      make: v.m,
      model: v.o,
      is_default: v.d
    }));
  }

  queueOperation(operation, data) {
    const queue = JSON.parse(localStorage.getItem('operation_queue') || '[]');
    queue.push({ operation, data, timestamp: Date.now() });
    localStorage.setItem('operation_queue', JSON.stringify(queue));
  }

  async processQueue() {
    if (!navigator.onLine) return;

    const queue = JSON.parse(localStorage.getItem('operation_queue') || '[]');
    if (queue.length === 0) return;

    const processed = [];
    const failed = [];

    for (const item of queue) {
      try {
        await this[item.operation](item.data);
        processed.push(item);
      } catch (error) {
        if (!error.message.includes('offline')) {
          failed.push(item);
        }
      }
    }

    // Remove processed items and keep failed ones
    const remaining = queue.filter(item => 
      !processed.includes(item) && !failed.includes(item)
    );
    
    if (remaining.length > 0) {
      localStorage.setItem('operation_queue', JSON.stringify(remaining));
    } else {
      localStorage.removeItem('operation_queue');
    }

    // Notify about failed operations
    if (failed.length > 0) {
      window.MyGarage.showNotification(
        `${failed.length} operations failed to process`,
        'error'
      );
    }
  }

  enforceVehicleLimit(vehicles) {
    const maxVehicles = window.MyGarageSettings?.maxVehicles || 4;
    
    if (vehicles.length > maxVehicles) {
      // Keep only the most recently added vehicles up to the limit
      const sorted = vehicles.sort((a, b) => {
        return new Date(b.added_at || 0) - new Date(a.added_at || 0);
      });
      
      return sorted.slice(0, maxVehicles);
    }
    
    return vehicles;
  }

  validateVehicleData(vehicle) {
    const errors = [];
    
    // Required fields
    ['year', 'make', 'model'].forEach(field => {
      if (!vehicle[field]) {
        errors.push(`${field} is required`);
      }
    });

    // Year validation
    const year = parseInt(vehicle.year);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < 1900 || year > currentYear + 1) {
      errors.push('Invalid year');
    }

    // Make/Model validation
    if (vehicle.make && !/^[a-zA-Z0-9\s-]+$/.test(vehicle.make)) {
      errors.push('Invalid make format');
    }
    if (vehicle.model && !/^[a-zA-Z0-9\s-]+$/.test(vehicle.model)) {
      errors.push('Invalid model format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

window.VehicleManager = new VehicleManager(); 